import type { Session, User } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { isSupabaseConfigured, supabase } from "../lib/supabase";

type AuthContextValue = {
  configured: boolean;
  loading: boolean;
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  profileError: string | null;
  refreshProfile: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
};

export type Profile = {
  id: string;
  display_name: string;
  status: "invited" | "active" | "suspended";
  global_role: "system_admin" | "auditor" | "member";
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(isSupabaseConfigured);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const confirmedProfileUserId = useRef<string | null>(null);
  const profileRequest = useRef(0);

  async function loadProfile(nextSession: Session | null, preserveConfirmedProfile = false) {
    const request = profileRequest.current + 1;
    profileRequest.current = request;
    if (!preserveConfirmedProfile) {
      confirmedProfileUserId.current = null;
      setProfile(null);
      setProfileError(null);
    }
    if (!supabase || !nextSession) return;

    const { data, error } = await supabase
      .from("profiles")
      .select("id,display_name,status,global_role")
      .eq("id", nextSession.user.id)
      .maybeSingle();

    if (profileRequest.current !== request) return;
    if (error) {
      if (preserveConfirmedProfile) return;
      setProfileError("Não foi possível confirmar o estado desta conta.");
      return;
    }
    if (!data) {
      confirmedProfileUserId.current = null;
      setProfile(null);
      setProfileError("Não foi possível confirmar o estado desta conta.");
      return;
    }
    confirmedProfileUserId.current = data.id;
    setProfileError(null);
    setProfile(data as Profile);
  }

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    void supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      await loadProfile(data.session);
      setLoading(false);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      const preservesConfirmedProfile = Boolean(
        nextSession && confirmedProfileUserId.current === nextSession.user.id,
      );
      setSession(nextSession);
      if (!nextSession) {
        profileRequest.current += 1;
        confirmedProfileUserId.current = null;
        setProfile(null);
        setProfileError(null);
        setLoading(false);
        return;
      }
      if (!preservesConfirmedProfile) setLoading(true);
      void loadProfile(nextSession, preservesConfirmedProfile).finally(() => {
        if (!preservesConfirmedProfile) setLoading(false);
      });
    });

    return () => data.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      configured: isSupabaseConfigured,
      loading,
      session,
      user: session?.user ?? null,
      profile,
      profileError,
      refreshProfile: async () => {
        setLoading(true);
        await loadProfile(session);
        setLoading(false);
      },
      signIn: async (email, password) => {
        if (!supabase) return "O backend ainda não foi configurado.";
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return error?.message ?? null;
      },
      signOut: async () => {
        if (supabase) await supabase.auth.signOut();
      },
    }),
    [loading, profile, profileError, session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth deve ser usado dentro de AuthProvider");
  return context;
}
